import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { T, Var, useGT } from 'gt-react';
import {
  EXTRACTED_CSV_FULL_READ_MAX_ROWS,
  type ExtractedCsvTable,
} from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PricelistCsvGrid } from './PricelistCsvGrid';
import { PricelistSearchBox } from './PricelistSearchBox';
import { PricelistSearchResults } from './PricelistSearchResults';
import { buildCsvDrafts } from './pricelistCsvEditState';
import { pricelistTableKey, pricelistTableLabel } from './pricelistTableLabel';
import { useStoredPricelistSearch } from './usePricelistSearch';

/** Rows fetched per page; must stay within the contract's window limit. */
const WINDOW_PAGE_SIZE = 500;

/**
 * Read-only, page-at-a-time view of a pricelist too large for the server to
 * return whole. The initial (windowless) fetch delivered every table's headers
 * and row count but no rows — this component pages the active table's rows
 * through `getExtractedCsv`'s window parameter, one bounded request per page.
 *
 * Editing is deliberately absent: saves post entire tables, so editing a
 * partial view would truncate the pricelist to whatever page was loaded.
 */
export function PricelistCsvWindowedView({
  csvDocumentId,
  tables,
  maxGridHeight,
}: {
  csvDocumentId: string;
  tables: ExtractedCsvTable[];
  /** Caps each page of rows so the page under the table can still scroll. */
  maxGridHeight?: number;
}) {
  const gt = useGT();
  const [activeIndex, setActiveIndex] = useState(0);
  const [pageByTable, setPageByTable] = useState<Record<number, number>>({});
  const [query, setQuery] = useState('');

  const search = useStoredPricelistSearch({ csvDocumentId, query, tables });

  const tableIndex = Math.min(activeIndex, Math.max(tables.length - 1, 0));
  const table = tables[tableIndex];
  const page = pageByTable[tableIndex] ?? 0;
  const pageCount = Math.max(
    1,
    Math.ceil((table?.rowCount ?? 0) / WINDOW_PAGE_SIZE),
  );

  const windowQuery = useQuery({
    ...orpc.inventoryManagement.pricelist.getExtractedCsv.queryOptions({
      input: {
        documentId: csvDocumentId,
        window: {
          table: tableIndex,
          offset: page * WINDOW_PAGE_SIZE,
          limit: WINDOW_PAGE_SIZE,
        },
      },
    }),
    // Keep the previous page on screen while the next one loads, so paging
    // doesn't flash the whole grid away.
    placeholderData: keepPreviousData,
    enabled: table !== undefined,
  });

  // While placeholder data is on screen, the rows belong to the previously
  // settled window, not the requested one — the grid, its headers, and the
  // row-range label must all keep describing that window until the new rows
  // land, or the label lies about what is visible.
  const [displayed, setDisplayed] = useState({ table: tableIndex, page });
  if (
    !windowQuery.isPlaceholderData &&
    (displayed.table !== tableIndex || displayed.page !== page)
  ) {
    setDisplayed({ table: tableIndex, page });
  }
  const displayedTable = tables[displayed.table];

  const rows = windowQuery.data?.tables[displayed.table]?.rows;
  const draft = useMemo(() => {
    if (!displayedTable) return undefined;
    return buildCsvDrafts([
      { headers: displayedTable.headers, rows: rows ?? [] },
    ])[0];
  }, [displayedTable, rows]);

  if (!table || !displayedTable || !draft) return null;

  const firstRow = displayed.page * WINDOW_PAGE_SIZE + 1;
  const lastRow = Math.min(
    (displayed.page + 1) * WINDOW_PAGE_SIZE,
    displayedTable.rowCount,
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md border px-4 py-2 text-xs text-muted-foreground">
        <ReadOnlyReason tables={tables} />
      </div>
      <PricelistSearchBox
        value={query}
        onChange={setQuery}
        isSearching={search.status === 'searching'}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {tables.length > 1 ? (
          <Tabs
            value={String(tableIndex)}
            onValueChange={(value) => {
              setActiveIndex(Number(value));
            }}
          >
            <TabsList>
              {tables.map((t, index) => (
                <TabsTrigger
                  key={pricelistTableKey(t.name, t.headers)}
                  value={String(index)}
                >
                  {pricelistTableLabel(gt, t.name, index)}
                  {search.hitCounts[index] === undefined ? null : (
                    <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] tabular-nums">
                      {search.hitCounts[index].toLocaleString()}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : (
          <span />
        )}
        <div
          className={search.searching ? 'hidden' : 'flex items-center gap-2'}
        >
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0 || windowQuery.isFetching}
            onClick={() =>
              setPageByTable((prev) => ({ ...prev, [tableIndex]: page - 1 }))
            }
          >
            <T context="previous page of a paginated pricelist table">
              Previous
            </T>
          </Button>
          <span className="text-xs text-muted-foreground">
            <T context="row range indicator for a paginated pricelist table">
              Rows <Var>{firstRow.toLocaleString()}</Var>–
              <Var>{lastRow.toLocaleString()}</Var> of{' '}
              <Var>{displayedTable.rowCount.toLocaleString()}</Var>
            </T>
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount - 1 || windowQuery.isFetching}
            onClick={() =>
              setPageByTable((prev) => ({ ...prev, [tableIndex]: page + 1 }))
            }
          >
            <T context="next page of a paginated pricelist table">Next</T>
          </Button>
        </div>
      </div>
      {search.searching ? (
        <PricelistSearchResults
          view={search}
          tableIndex={tableIndex}
          query={query}
          maxGridHeight={maxGridHeight}
        />
      ) : null}
      {windowQuery.isPending ? (
        <div className="flex flex-col gap-2 rounded-md border px-4 py-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      ) : windowQuery.isError ? (
        <div className="rounded-md border px-4 py-3 text-sm text-destructive">
          {gt('Failed to load this page of rows.', {
            context:
              'error state when a page of a paginated pricelist table fails to load',
          })}
        </div>
      ) : (
        // Hidden rather than unmounted while search results take its place:
        // rebuilding Handsontable over a whole page of rows takes long enough to
        // read as a freeze when the search is cleared.
        //
        // Dimmed while the rows on screen are the previous window's, so the
        // stale page reads as loading rather than as the requested one.
        <div
          className={[
            search.searching ? 'hidden' : '',
            windowQuery.isPlaceholderData
              ? 'pointer-events-none opacity-60'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <PricelistCsvGrid
            key={`${displayed.table}:${displayed.page}`}
            draft={draft}
            dataVersion={windowQuery.dataUpdatedAt}
            readOnly
            maxHeight={maxGridHeight}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Why the pricelist is read-only. The server's limits apply to the whole file —
 * every table's rows together, or their bytes — so the notice states totals,
 * never the open tab's row count (a 21-row tab beside a big one is not "too
 * many rows").
 */
function ReadOnlyReason({ tables }: { tables: ExtractedCsvTable[] }) {
  const totalRows = tables.reduce((sum, table) => sum + table.rowCount, 0);
  const rows = totalRows.toLocaleString();
  if (totalRows <= EXTRACTED_CSV_FULL_READ_MAX_ROWS) {
    return (
      <T context="notice above a read-only paginated pricelist whose cell content is too large (in bytes) to edit inline">
        This pricelist holds too much data to edit inline, so it is shown
        read-only, one page at a time. Download the CSV for the whole file.
      </T>
    );
  }
  if (tables.length > 1) {
    return (
      <T context="notice above a read-only paginated pricelist split into several tables whose combined row count is too large to edit inline">
        This pricelist has <Var>{tables.length}</Var> tables with{' '}
        <Var>{rows}</Var> rows in total — too many to edit inline, so each table
        is shown read-only, one page at a time. Download the CSV for the whole
        file.
      </T>
    );
  }
  return (
    <T context="notice above a read-only paginated pricelist table too large to edit inline">
      This pricelist has <Var>{rows}</Var> rows — too many to edit inline, so it
      is shown read-only, one page at a time. Download the CSV for the whole
      file.
    </T>
  );
}
